import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Download, ClipboardList, Columns2, Rows3 } from "lucide-react";
import * as api from "../../lib/uploadsApi";
import { headers } from "../../lib/classificationApi";
import { useRole } from "../../lib/role.jsx";
import ReviewSteps from "./ReviewSteps";
import ReviewGate from "./ReviewGate";
import ReviewProfile from "./ReviewProfile";
import { RowsGrid, BeforeAfter } from "./ReviewRows";
import RuleEvidence from "./RuleEvidence";
import { EVIDENCE } from "./DecideBox";
import { StageTrack, StatusPill, Pill, OPEN, SOURCE, when, num } from "./Layer";
import { cn } from "../../lib/utils";

const VIEWS = [["profile", "Review", ClipboardList], ["compare", "Before / after · evidence", Columns2], ["rows", "Rows", Rows3]];

function Outcome({ it }) {
  if (it.status === "rejected") return <div className="border border-critical/40 bg-critical/5 rounded-sm px-3 py-2 text-[12.5px]" data-testid="rv-outcome">Rejected by {it.rejected_by} · {when(it.rejected_at)} — {it.reject_reason}</div>;
  if (it.status === "silver") return <div className="border border-success/40 bg-success/5 rounded-sm px-3 py-2 text-[12.5px] space-y-1" data-testid="rv-outcome">
    <div>Promoted to Silver by {it.promoted_by} · {when(it.promoted_at)} ({it.mode}){it.promote_note ? ` — ${it.promote_note}` : ""}</div>
    {(it.clean_log || []).map(l => <div key={l} className="text-muted text-[11.5px]">· {l}</div>)}</div>;
  if (it.status === "too_large") return <div className="border border-warning/40 bg-warning/5 rounded-sm px-3 py-2 text-[12.5px]" data-testid="rv-outcome">This S3 file is larger than the review limits (50 MB / 1 million rows). It waits for large-file review in Phase 2.</div>;
  return null;
}

function Head({ it, isIT, onClose }) {
  return (
    <div className="sticky top-0 bg-bg/95 backdrop-blur-md border-b border-border px-4 md:px-5 py-3.5 flex items-start gap-3 z-20">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-[10px] uppercase tracking-[0.22em] text-primary font-semibold">Bronze review</span>
          <StageTrack stage={it?.status === "silver" ? "silver" : "bronze"} testid="rv-stage" />
          {it && <StatusPill status={it.status} testid="rv-status" />}
          {it && <Pill tone="neutral" testid="rv-source">{SOURCE[it.source] || "Upload"}</Pill>}
        </div>
        <h2 className="font-display text-lg font-semibold tracking-tight truncate" data-testid="rv-title">{it ? `${it.filename}${it.sheet ? ` · ${it.sheet}` : ""}` : "Loading…"}</h2>
        {it && <div className="text-[11.5px] text-muted font-mono-tabular truncate">{num(it.profile?.rows)} rows · brought in by {it.uploaded_by} · {when(it.uploaded_at)}{it.source === "s3" && it.s3 ? ` · s3://${it.s3.bucket}/${it.s3.key}` : ""}</div>}
      </div>
      {isIT && it?.storage_path && <button data-testid="rv-download" onClick={() => fetchDownload(it)} className="h-8 px-2.5 inline-flex items-center gap-1.5 border border-border rounded-sm text-[12px] hover:border-primary"><Download size={13} />Original</button>}
      <button data-testid="rv-close" onClick={onClose} aria-label="Close" className="h-8 w-8 shrink-0 inline-flex items-center justify-center border border-border rounded-sm hover:border-primary"><X size={15} /></button>
    </div>
  );
}

async function fetchDownload(it) {
  const res = await fetch(api.downloadUrl(it.id), { headers: headers(null, false) });
  const url = URL.createObjectURL(await res.blob());
  Object.assign(document.createElement("a"), { href: url, download: it.filename }).click();
  URL.revokeObjectURL(url);
}

function SampleNote({ s }) {
  if (!s) return null;
  return (
    <div className="border border-info/40 bg-info/5 rounded-sm px-3 py-2.5 text-[12.5px] flex gap-2.5" data-testid="rv-sample-banner">
      <Rows3 size={14} className="text-info mt-0.5 shrink-0" />
      <div className="space-y-0.5">
        <div className="font-semibold" data-testid="rv-sample-text">Reviewing a sample of {num(s.rows)} of {num(s.total)} rows</div>
        <div className="text-muted text-[11.5px] leading-snug">Rows are taken from the start, middle and end of the file, so quality and row counts here are from the sample.
          When you promote, the cleaning rules, approved fixes and saved steps run on all {num(s.total)} rows and a final check covers every row. Hand edits and removals only change the sampled rows.</div>
      </div>
    </div>
  );
}

const wait = (ms) => new Promise(r => setTimeout(r, ms));

// Wide review & cleansing panel for one Bronze item: steps on the left, profile / before-after / rows on the right.
export default function BronzeReview({ id, meta, onClose, onDone }) {
  const { role } = useRole();
  const curator = ["it_head", "coo"].includes(role?.id);
  const isIT = role?.id === "it_head";
  const [d, setD] = useState(null);
  const [item, setItem] = useState(null);
  const [error, setError] = useState(null);
  const [sel, setSel] = useState(null);
  const [view, setView] = useState("profile");
  const [nonce, setNonce] = useState(0);
  const [flash, setFlash] = useState(null);
  const [prog, setProg] = useState(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const load = useCallback(() => api.getReview(id).then(x => { setD(x); setItem(x.item); setError(null); setNonce(n => n + 1); })
    .catch(e => { setError(e.message); api.getItem(id).then(setItem).catch(() => {}); }), [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const act = async (fn, rethrow = false, msg = null) => {
    try {
      const r = await fn();
      if (r && r.item && r.rules) { setD(r); setItem(r.item); setNonce(n => n + 1); } else await load();
      if (msg) { setFlash(msg); setTimeout(() => setFlash(null), 2600); }
    } catch (e) {
      if (rethrow) throw e;
      setFlash(e.message); setTimeout(() => setFlash(null), 4000);
    }
  };
  const open = d && OPEN.includes(d.item.status);
  const canEdit = curator && open;
  const typeLabel = d ? meta.types[d.item.type]?.label : null;
  const pick = (s) => { setSel(s); setView("compare"); };
  const watch = useCallback(async () => {
    while (alive.current) {
      const p = await api.getPromotion(id);
      if (p.state !== "running") return p;
      if (alive.current) setProg(p);
      await wait(1000);
    }
    return null;
  }, [id]);
  const resuming = d?.item?.promotion?.state === "running" && !prog;
  useEffect(() => {
    if (!resuming) return;
    watch().then(p => { if (!p) return; setProg(null); p.state === "done" ? onDone(`Promotion finished · ${num(p.result?.rows_added)} rows added to Silver`) : load(); }).catch(() => setProg(null));
  }, [resuming, watch, load, onDone]);
  const promote = async (mode, note) => {
    setProg({ phase: "reading", done: 0, total: item?.profile?.rows });
    const tick = setInterval(() => api.getPromotion(id).then(p => alive.current && p.state === "running" && setProg(p)).catch(() => {}), 1000);
    try {
      let r = await api.promoteItem(id, mode, note);
      clearInterval(tick);
      if (r.state === "running") {
        const p = await watch();
        if (!p) return;
        if (p.state !== "done") throw new Error(p.error || "The promotion stopped — promote it again.");
        r = p.result;
      }
      if (alive.current) onDone(`Promoted to Silver ${typeLabel || ""} · ${num(r.rows_added)} rows added${r.overlap_skipped ? `, ${num(r.overlap_skipped)} already there` : ""}`);
    } catch (e) { if (alive.current) load(); throw e; } finally { clearInterval(tick); if (alive.current) setProg(null); }
  };
  const reject = async (reason) => { await api.rejectItem(id, reason); onDone("Rejected — the uploader sees your reason."); };
  return createPortal(
    <div className="fixed inset-0 z-[60]" data-testid="bronze-review">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-full w-full lg:w-[96vw] 2xl:w-[1560px] bg-bg border-l border-border shadow-2xl overflow-y-auto fc-slide-in">
        <Head it={item} isIT={isIT} onClose={onClose} />
        {!d && !error && <div className="py-16 text-center text-[12px] text-muted"><Loader2 size={14} className="inline animate-spin mr-2" />Opening the working copy…</div>}
        {!d && error && <div className="m-5 space-y-3"><div className="text-[12.5px] border border-warning/40 bg-warning/5 text-warning rounded-sm p-3" data-testid="rv-error">{error}</div>{item && <Outcome it={item} />}</div>}
        {d && (
          <div className="p-4 md:p-5 space-y-4">
            <Outcome it={d.item} />
            <SampleNote s={d.sample} />
            <div className="grid grid-cols-1 lg:grid-cols-[400px_minmax(0,1fr)] gap-5">
              <div className="space-y-5 lg:border-r lg:border-border lg:pr-5">
                <ReviewSteps d={d} typeLabel={typeLabel} canEdit={canEdit} sel={sel} onSel={pick} act={act} api={api} />
                <ReviewGate d={d} canEdit={canEdit} typeLabel={typeLabel} prog={prog} onPromote={promote} onReject={reject} />
              </div>
              <div className="min-w-0 space-y-3">
                <div className="flex flex-wrap gap-1 border-b border-border pb-2" data-testid="rv-views">
                  {VIEWS.map(([k, l, Icon]) => (
                    <button key={k} data-testid={`rv-view-${k}`} onClick={() => setView(k)}
                            className={cn("h-8 px-3 inline-flex items-center gap-1.5 rounded-sm text-[12.5px] transition-colors", view === k ? "bg-primary text-primary-fg font-semibold" : "text-muted hover:bg-surface-2 hover:text-fg")}>
                      <Icon size={13} />{l}</button>))}
                  <span className="ml-auto self-center text-[11.5px] text-muted font-mono-tabular" data-testid="rv-flow">{d.sample ? `${num(d.rows_in)} of ${num(d.sample.total)} rows sampled` : `${num(d.rows_in)} raw`} → {num(d.rows_out)} after cleaning</span>
                </div>
                {view === "profile" && <ReviewProfile d={d} meta={meta} canMap={canEdit} canGuide={curator} onChanged={load} />}
                {view === "compare" && (sel?.kind === "rule" && EVIDENCE.includes(sel.id)
                  ? <RuleEvidence key={sel.id} d={d} rule={sel.id} canEdit={canEdit} act={act} api={api} nonce={nonce} /> : <BeforeAfter d={d} sel={sel} />)}
                {view === "rows" && <RowsGrid d={d} canEdit={canEdit} canFull={isIT} nonce={nonce} act={act} api={api} />}
              </div>
            </div>
          </div>)}
        {flash && <div data-testid="rv-flash" className="fixed bottom-6 right-6 z-[70] bg-surface border border-border-strong rounded-sm shadow-lg px-4 py-3 text-sm max-w-sm">{flash}</div>}
      </aside>
    </div>, document.body);
}
